import { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { getIngredients } from "../../insumos/api/ingredientsApi";
import { getPreparedProducts } from "../../elaborados/api/preparedProductsApi";
import { componentKey } from "../utils/recipeForm.utils";

const SEARCH_DELAY_MS = 150;
const RESULTS_PER_TYPE = 10;

const UNIT_LABELS = {
  g: "g",
  ml: "ml",
  unit: "unidad",
  package: "paquete",
};

function rowsFromResponse(response) {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response)) return response;
  return [];
}

function errorToMessage(error) {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.data?.message ||
    error?.message ||
    "No se pudieron buscar componentes."
  );
}

function normalizeIngredient(ingredient) {
  return {
    kind: "ingredient",
    group: "Insumos",
    id: ingredient.id,
    name: ingredient.name || `Insumo #${ingredient.id}`,
    unit: ingredient.baseUnit || "",
    unitLabel: UNIT_LABELS[ingredient.baseUnit] || ingredient.baseUnit || "sin unidad",
    isAvailable: true,
  };
}

function normalizePreparedProduct(preparedProduct) {
  return {
    kind: "preparedProduct",
    group: "Elaborados",
    id: preparedProduct.id,
    name: preparedProduct.name || `Elaborado #${preparedProduct.id}`,
    unit: preparedProduct.resultUnit || "",
    unitLabel:
      UNIT_LABELS[preparedProduct.resultUnit] ||
      preparedProduct.resultUnit ||
      "sin unidad",
    isAvailable: true,
  };
}

export default function RecipeComponentSearch({
  selected = [],
  disabled,
  disabledKinds = [],
  onSelect,
}) {
  const latestRequestId = useRef(0);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedKeys = useMemo(
    () => new Set(selected.map(componentKey)),
    [selected]
  );
  const visibleOptions = useMemo(
    () =>
      options.filter(
        (option) =>
          !selectedKeys.has(componentKey(option)) &&
          !disabledKinds.includes(option.kind)
      ),
    [disabledKinds, options, selectedKeys]
  );

  useEffect(() => {
    const query = inputValue.trim();
    if (!query) {
      latestRequestId.current += 1;
      setOptions([]);
      setLoading(false);
      setError("");
      return undefined;
    }

    const controller = new AbortController();
    const requestId = ++latestRequestId.current;
    setLoading(true);
    setError("");
    const timeout = setTimeout(async () => {
      try {
        const [ingredientsResult, preparedProductsResult] = await Promise.allSettled([
          getIngredients({
            page: 1,
            limit: RESULTS_PER_TYPE,
            name: query,
            isActive: true,
            signal: controller.signal,
          }),
          getPreparedProducts({
            page: 1,
            limit: RESULTS_PER_TYPE,
            name: query,
            isActive: true,
            signal: controller.signal,
          }),
        ]);
        if (controller.signal.aborted || requestId !== latestRequestId.current) return;

        const requestErrors = [ingredientsResult, preparedProductsResult]
          .filter((result) => result.status === "rejected")
          .map((result) => errorToMessage(result.reason));
        const ingredientsResponse =
          ingredientsResult.status === "fulfilled" ? ingredientsResult.value : [];
        const preparedProductsResponse =
          preparedProductsResult.status === "fulfilled"
            ? preparedProductsResult.value
            : [];

        setOptions([
          ...rowsFromResponse(ingredientsResponse)
            .filter((item) => item?.id != null)
            .map(normalizeIngredient),
          ...rowsFromResponse(preparedProductsResponse)
            .filter((item) => item?.id != null)
            .map(normalizePreparedProduct),
        ]);
        setError(requestErrors.join(" | "));
      } catch (requestError) {
        if (controller.signal.aborted || requestId !== latestRequestId.current) return;
        setOptions([]);
        setError(errorToMessage(requestError));
      } finally {
        if (!controller.signal.aborted && requestId === latestRequestId.current) {
          setLoading(false);
        }
      }
    }, SEARCH_DELAY_MS);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [inputValue]);

  return (
    <Autocomplete
      value={null}
      inputValue={inputValue}
      options={visibleOptions}
      loading={loading}
      disabled={disabled}
      open={Boolean(inputValue.trim())}
      filterOptions={(currentOptions) => currentOptions}
      groupBy={(option) => option.group}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => componentKey(option) === componentKey(value)}
      noOptionsText={error || "No se encontraron insumos ni elaborados"}
      loadingText="Buscando insumos y elaborados..."
      onInputChange={(_, value, reason) => {
        if (reason === "input" || reason === "clear") setInputValue(value);
      }}
      onChange={(_, option) => {
        if (!option) return;
        onSelect(option);
        setInputValue("");
        setOptions([]);
      }}
      renderGroup={(params) => (
        <li key={params.key}>
          <Box
            sx={{
              position: "sticky",
              top: -8,
              zIndex: 1,
              px: 1.5,
              py: 0.7,
              bgcolor: "secondary.100",
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 900, color: "primary.900" }}>
              {params.group}
            </Typography>
          </Box>
          <Box component="ul" sx={{ p: 0, m: 0 }}>
            {params.children}
          </Box>
        </li>
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box
            component="li"
            key={`${option.kind}:${option.id}`}
            {...optionProps}
            sx={{ display: "flex", gap: 2 }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: 13, fontWeight: 700 }}>
                {option.name}
              </Typography>
            </Box>
            <Typography
              sx={{
                flexShrink: 0,
                alignSelf: "center",
                fontSize: 11,
                fontWeight: 600,
                color: "neutral.600",
              }}
            >
              {option.unitLabel}
            </Typography>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Escribí para buscar un insumo o elaborado"
          error={Boolean(error)}
          helperText={error}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <SearchIcon sx={{ ml: 1, mr: 0.5, fontSize: 19, color: "primary.900" }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={17} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "white",
              fontSize: 13,
            },
            "& .MuiFormHelperText-root": { mx: 0.5, fontSize: 10.5 },
          }}
        />
      )}
    />
  );
}
